import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";

const SESSION_KEY = "ipv_chat_session";

const getSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
};

export default function SupportChat() {
  const { pathname } = useLocation();
  const tallBar = pathname.startsWith("/build-pallet"); // taller sticky progress bar there
  const mobileLift = tallBar ? "bottom-[150px]" : "bottom-[92px]";
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api
      .get(`/chat/support/${getSession()}`)
      .then((r) => {
        const m = r.data.messages || [];
        setMsgs(
          m.length
            ? m
            : [{ role: "assistant", content: "Hi! I'm Val 👋 — ask me about delivery fees, store hours, holds, e-bikes, or anything in the store." }]
        );
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat/support", { session_id: getSession(), message: text });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, something went wrong — call us at (616) 410-0158!" }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className={`fixed ${mobileLift} right-4 md:bottom-5 md:right-5 z-30 md:z-50 bg-[#0A1C40] text-[#FFC220] rounded-full p-3.5 md:p-4 shadow-2xl hover:scale-105 transition-transform`}
          aria-label="Chat with us"
          data-testid="support-chat-open"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      )}
      {open && (
        <div
          className={`fixed ${mobileLift} right-4 md:bottom-5 md:right-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden`}
          style={{ height: "min(560px, calc(100vh - 11rem))" }}
          data-testid="support-chat-panel"
        >
          <div className="bg-[#0A1C40] text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div>
              <div className="font-display font-black">Val · IPVALUE Support</div>
              <div className="text-[10px] text-white/60">Usually replies in seconds · (616) 410-0158</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="support-chat-close" aria-label="Close chat">
              <X className="w-5 h-5 opacity-70 hover:opacity-100" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-line rounded-2xl ${
                    m.role === "user"
                      ? "bg-[#0A1C40] text-white rounded-br-sm"
                      : "bg-white border border-gray-200 text-gray-800 rounded-bl-sm"
                  }`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="p-2 border-t border-gray-200 flex gap-2 shrink-0 bg-white">
            <input
              className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-sm outline-none focus:border-[#0A1C40]"
              placeholder="Ask about delivery, hours, e-bikes…"
              value={input}
              maxLength={500}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              data-testid="support-chat-input"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              className="bg-[#FFC220] text-[#0A1C40] rounded-full p-2.5 disabled:opacity-40"
              data-testid="support-chat-send"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
